'use client';

/**
 * /for-advocates — inbound + outbound landing page for advocates.
 *
 * Flow:
 *   idle    → form visible, nothing streaming
 *   searching → SearchProgress visible, fetch in flight
 *   done    → ResultsGrouped visible (placeholder until Task 14)
 *   error   → error banner with retry
 *
 * Pre-fills name/city/state/BCI from query params so outbound links can
 * land directly on the form. Keeps search state in URL-shareable fashion
 * via `sessionId` once we have one — that lets the results page survive
 * refreshes and OAuth round-trips.
 */

import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import SearchForm, { type SearchFormValues } from './components/SearchForm';
import SearchProgress from './components/SearchProgress';
import ResultsGrouped from './components/ResultsGrouped';
import ClaimModal from './components/ClaimModal';
import {
  INITIAL_COURT_PROGRESS,
  type AdvocateCourt,
  type CourtProgressMap,
  type ResultGroup,
  type SearchStreamEvent,
} from './types';

type Phase = 'idle' | 'searching' | 'done' | 'error';

interface SearchResult {
  sessionId: string | null;
  groups: ResultGroup[];
  totalCases: number;
  activeCases: number;
}

interface AccessState {
  allowed: boolean;
  reason: string;
  redirectTo?: string;
  message?: string;
}

export default function ForAdvocatesPage() {
  return (
    <Suspense fallback={<PageShell><div className="py-24 text-center text-ink-4">Loading…</div></PageShell>}>
      <ForAdvocates />
    </Suspense>
  );
}

function ForAdvocates() {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Prefill from outbound-link query params.
  const initial: Partial<SearchFormValues> = {
    name: searchParams.get('name') ?? undefined,
    city: searchParams.get('city')?.toLowerCase() ?? undefined,
    state: searchParams.get('state') ?? undefined,
    barCouncilId: searchParams.get('bci') ?? undefined,
    source: searchParams.get('src') ?? undefined,
  };

  const [phase, setPhase] = useState<Phase>('idle');
  const [submitted, setSubmitted] = useState<SearchFormValues | null>(null);
  const [progress, setProgress] = useState<CourtProgressMap>(INITIAL_COURT_PROGRESS);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const [claimSelection, setClaimSelection] = useState<{ caseKeys: string[]; groupKeys: string[] } | null>(null);
  const [access, setAccess] = useState<AccessState | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Preflight entitlement check — decides whether to show the form, or route
  // the visitor to sign-in / pricing / request-access instead.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/advocate-search/access', { cache: 'no-store' });
        const data = (await res.json()) as AccessState;
        if (!cancelled) setAccess(data);
      } catch {
        if (!cancelled) setAccess({ allowed: false, reason: 'upstream_unavailable' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // If we landed here with ?session=<id> (e.g., OAuth round-trip), hydrate
  // the prior result without re-running any scrapes.
  const sessionFromUrl = searchParams.get('session');
  useEffect(() => {
    if (!sessionFromUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/advocate-search/session/${sessionFromUrl}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setSubmitted({ name: data.queryName, city: data.city, state: data.state });
        setResult({
          sessionId: data.sessionId,
          groups: data.groups ?? [],
          totalCases: data.totalCases ?? 0,
          activeCases: data.activeCases ?? 0,
        });
        setProgress({
          district: { status: 'done', count: undefined },
          high_court: { status: 'done', count: undefined },
          supreme_court: { status: 'done', count: undefined },
          tribunal: { status: 'done', count: undefined },
        });
        setPhase('done');
      } catch { /* best-effort hydration */ }
    })();
    return () => { cancelled = true; };
  }, [sessionFromUrl]);

  const runSearch = useCallback(async (values: SearchFormValues) => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setSubmitted(values);
    setResult(null);
    setErrorMsg(null);
    setProgress({
      district: { status: 'searching' },
      high_court: { status: 'searching' },
      supreme_court: { status: 'searching' },
      tribunal: { status: 'searching' },
    });
    setPhase('searching');

    try {
      const res = await fetch('/api/advocate-search', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(values),
        signal: ac.signal,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 401 || res.status === 403) {
          // Re-read access so the gate UI re-renders with the right CTA.
          setAccess({
            allowed: false,
            reason: body?.error || 'auth_required',
            redirectTo: body?.redirectTo,
            message: body?.message,
          });
          setPhase('idle');
          setErrorMsg(null);
          return;
        }
        if (res.status === 429) {
          setErrorMsg("You've submitted a lot of searches recently. Please wait a minute and try again.");
        } else if (res.status === 503) {
          setErrorMsg('Our search is offline for a moment. Please try again in a few seconds.');
        } else {
          setErrorMsg(body?.message || body?.error || 'Search could not start. Please try again.');
        }
        setPhase('error');
        return;
      }
      if (!res.body) {
        setErrorMsg('Your browser does not support this page. Please update to the latest version.');
        setPhase('error');
        return;
      }

      const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += value;
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? ''; // keep trailing partial line
        for (const raw of lines) {
          const trimmed = raw.trim();
          if (!trimmed) continue;
          let event: SearchStreamEvent;
          try { event = JSON.parse(trimmed) as SearchStreamEvent; } catch { continue; }
          if (event.type === 'court_done') {
            const court = event.court as AdvocateCourt;
            setProgress(prev => ({
              ...prev,
              [court]: {
                status: event.reason ? 'error' : 'done',
                count: event.count,
                reason: event.reason,
              },
            }));
          } else if (event.type === 'results') {
            setResult({
              sessionId: event.sessionId ?? null,
              groups: event.groups,
              totalCases: event.totalCases,
              activeCases: event.activeCases,
            });
            setPhase('done');
            // Put session id in URL so reloads re-hydrate.
            if (event.sessionId) {
              const next = new URLSearchParams(searchParams.toString());
              next.set('session', event.sessionId);
              router.replace(`/for-advocates?${next.toString()}`, { scroll: false });
            }
          }
        }
      }
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      console.error('[for-advocates] search failed:', err);
      setErrorMsg('Something went wrong. Please try again.');
      setPhase('error');
    }
  }, [router, searchParams]);

  useEffect(() => () => abortRef.current?.abort(), []);

  return (
    <PageShell>
      <Hero />

      <div className="mx-auto max-w-2xl px-4 pb-24 sm:px-6">
        {(phase === 'idle' || phase === 'error') && (
          <div className="rounded-xl border border-line bg-white p-6 shadow-sm sm:p-8">
            {phase === 'error' && errorMsg && (
              <div className="mb-5 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {errorMsg}
              </div>
            )}
            {access && !access.allowed ? (
              <AccessGate access={access} initial={initial} />
            ) : (
              <SearchForm initial={initial} disabled={!access} onSubmit={runSearch} />
            )}
          </div>
        )}

        {(phase === 'searching' || phase === 'done') && submitted && (
          <SearchProgress
            progress={progress}
            queryName={submitted.name}
            city={submitted.city}
          />
        )}

        {phase === 'done' && result && (
          <div className="mt-8">
            <ResultsGrouped
              groups={result.groups}
              totalCases={result.totalCases}
              activeCases={result.activeCases}
              onClaim={(caseKeys, groupKeys) => {
                if (!result.sessionId) {
                  setErrorMsg('Your search session has expired. Please try again.');
                  setPhase('error');
                  return;
                }
                setClaimSelection({ caseKeys, groupKeys });
                setClaimOpen(true);
              }}
            />
          </div>
        )}

        {claimSelection && result?.sessionId && submitted && (
          <ClaimModal
            open={claimOpen}
            onClose={() => setClaimOpen(false)}
            sessionId={result.sessionId}
            selectedCaseKeys={claimSelection.caseKeys}
            selectedGroupKeys={claimSelection.groupKeys}
            queryName={submitted.name}
            onClaimed={() => setClaimOpen(false)}
          />
        )}
      </div>

      <TrustStrip />
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// Sub-pieces — kept inline until there's a second consumer.
// ---------------------------------------------------------------------------

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-paper">
      {children}
    </main>
  );
}

function Hero() {
  return (
    <section className="bg-primary text-white">
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24">
        <div className="eyebrow text-primary-300 mb-5">
          For advocates
        </div>
        <h1 className="font-serif text-4xl leading-[1.05] tracking-[-0.02em] sm:text-5xl md:text-6xl">
          Your cases. <span className="italic text-primary-300">Already tracked.</span>
        </h1>
        <p className="mt-6 max-w-xl text-base text-primary-200/90 sm:text-lg">
          Enter your name and city. We pre-populate your dashboard with matters filed
          under you at district courts, your High Court, and the Supreme Court.
        </p>
      </div>
    </section>
  );
}

function AccessGate({
  access,
  initial,
}: {
  access: AccessState;
  initial: Partial<SearchFormValues>;
}) {
  const qs = new URLSearchParams();
  if (initial.name) qs.set('name', initial.name);
  if (initial.city) qs.set('city', initial.city);
  if (initial.state) qs.set('state', initial.state);
  if (initial.barCouncilId) qs.set('bci', initial.barCouncilId);
  const preservedSearch = qs.toString();

  const ctaPrimary = (() => {
    switch (access.reason) {
      case 'auth_required':
        return {
          href: `/signin?redirect=${encodeURIComponent(`/for-advocates${preservedSearch ? `?${preservedSearch}` : ''}`)}`,
          label: 'Sign in',
        };
      case 'approval_pending':
        return { href: '/request-access', label: 'Check approval status' };
      case 'approval_expired':
      case 'subscription_required':
      case 'tier_too_low':
      default:
        return { href: '/pricing', label: 'See plans' };
    }
  })();

  const headline = (() => {
    switch (access.reason) {
      case 'auth_required': return 'Sign in to search your cases.';
      case 'approval_pending': return 'Your trial is being reviewed.';
      case 'approval_expired': return 'Your trial has ended.';
      case 'tier_too_low': return 'Case search is on Lawyer and Firm plans.';
      default: return 'Case search is available to subscribers.';
    }
  })();

  const secondary = access.reason === 'auth_required' ? null : (
    <Link
      href={`/signin?redirect=${encodeURIComponent(`/for-advocates${preservedSearch ? `?${preservedSearch}` : ''}`)}`}
      className="text-sm font-medium text-primary-700 underline-offset-4 hover:underline"
    >
      Sign in instead
    </Link>
  );

  return (
    <div className="space-y-5">
      <div>
        <p className="eyebrow mb-2">Restricted</p>
        <h2 className="font-serif text-2xl text-ink">{headline}</h2>
        {access.message && (
          <p className="mt-2 text-sm text-ink-3">{access.message}</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <Link
          href={ctaPrimary.href}
          className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-primary-700"
        >
          {ctaPrimary.label} →
        </Link>
        {secondary}
      </div>
    </div>
  );
}

function TrustStrip() {
  return (
    <section className="border-t border-line bg-paper-elev">
      <div className="mx-auto max-w-3xl px-4 py-10 text-center sm:px-6">
        <p className="text-sm text-ink-3">
          Already a user?{' '}
          <Link href="/signin" className="font-medium text-primary-700 underline-offset-4 hover:underline">
            Sign in
          </Link>
          {' '}to add more courts to your workspace.
        </p>
        <p className="mt-3 text-xs text-ink-4">
          Cases are sourced from the official public records maintained by the courts.
        </p>
      </div>
    </section>
  );
}
